import React from 'react';
import { Select, SelectItem } from '@heroui/react';
import { Package, Globe } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { FiltersBar, FilterField, ProductSelector } from '../base';
import { useLanguage } from '../../contexts/LanguageContext';

interface GuidesFiltersBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedProductId?: string;
  onProductChange: (productId: string | undefined) => void;
  selectedLanguage?: string;
  onLanguageChange: (language: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const GuidesFiltersBar: React.FC<GuidesFiltersBarProps> = ({
  searchQuery,
  onSearchChange,
  selectedProductId,
  onProductChange,
  selectedLanguage,
  onLanguageChange,
  onClearFilters,
  hasActiveFilters,
}) => {
  const { t } = useTranslation('guides');
  const { availableLanguages } = useLanguage();

  return (
    <FiltersBar
      title={t('filters.title')}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('filters.searchPlaceholder')}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      clearFiltersLabel={t('filters.clearAll')}
      gridCols="3"
    >
      <FilterField label={t('filters.product')} icon={Package}>
        <ProductSelector
          value={selectedProductId || 'all'}
          onChange={(value) => onProductChange(value === 'all' ? undefined : value)}
          placeholder={t('filters.allProducts')}
          variant="bordered"
          label=""
          showAllOption={true}
          className="w-full"
        />
      </FilterField>

      <FilterField label={t('filters.language')} icon={Globe}>
        <Select
          placeholder={t('filters.selectLanguage')}
          selectedKeys={selectedLanguage ? [selectedLanguage] : []}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as string;
            onLanguageChange(selected || 'en');
          }}
          variant="bordered"
          label=""
          className="w-full"
          selectionMode="single"
          classNames={{
            trigger:
              'border-gray-300 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-600 transition-colors',
          }}
        >
          <>
            <SelectItem key="all">{t('filters.allLanguages')}</SelectItem>
            {availableLanguages.map((lang) => (
              <SelectItem key={lang.code}>{lang.name}</SelectItem>
            ))}
          </>
        </Select>
      </FilterField>
    </FiltersBar>
  );
};

export default GuidesFiltersBar;
